import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { FontIcon, Button, IconButton } from 'react-toolbox';
import { has, find, filter, isEmpty } from 'lodash';
import Validator from 'mixins/validator';
import Resetter from 'mixins/resetter';
import SimpleInput from 'components/SimpleInput';
import ReactSelect from 'components/StyledSelect/react_select';
import CheckBox from 'components/CheckBox';
import ItemNote from 'components/LineItemTable/itemNote';
import PhaseType from 'types/phase';
import {
  getRandomNegativeInt, calculateTotal, formatDollars, filterByVendor,
} from '../../../../../../../utils/lineItemUtils';
import { getPhaseErrors } from '../../../../../../../reducers';
import { getPricePatchItems } from '../../../../../../../selectors/phaseLineItem';
import S from 'modal.scss';

const DISPLAY_KEYS = ['Number - Description', 'QTY', 'Unit Price', 'Total', 'Not Payable', 'Note', 'Delete'];

class PoDetails extends Resetter(Validator(React.Component)) {
  constructor(props) {
    super(props);
    const { number = '', phase = {}, total = 0, lineItems = [], reason = '', contact = '' } = this.props;
    this.state = {
      number,
      phase,
      total,
      lineItems,
      addRow: false,
      lineItemId: '',
      kind: '',
      quantity: 0,
      pricePatch: 0,
      lineItemTotal: 0,
      payable: true,
      note: '',
      reason,
      contact,
      errors: {},
    };
  }

  // TODO: Will be deprecated in React 17
  componentWillMount = () => this.props.setPoDetailsMounted(true);

  componentWillUnmount = () => this.props.setPoDetailsMounted(false);

  sanitize = (attr, val) => {
    let value = has(val, 'target') ? val.target.value : val;
    if (attr === 'lineItemTotal') value = value ? parseFloat(value) : '';
    return value;
  }

  getSelectedItem = val => find(this.props.lineItemOptions, o => o.value === val) || {};

  getInitialId = (lineItems) => {
    const ids = [];
    lineItems.forEach(i => ids.push(i.id));
    let id = getRandomNegativeInt();
    if (isEmpty(ids)) return id;
    while (ids.includes(id)) id = getRandomNegativeInt();
    return id;
  }

  validateNumber = (number) => {
    const regex = /^(?!00)\d{9}$|^PR\d{8}$|^\d{8}[AIW]$|^PRJ\d+$/;
    const ime_regex = /^\d{8}[AIW]$/;
    const rf_regex = /^PRJ\d+$/;
    const isProjectNumber = this.props.projectNumber === number
      && !this.props.projectNumber.match(ime_regex)
      && !this.props.projectNumber.match(rf_regex);
    return (number === null || number === '' || (regex.test(number))) && !isProjectNumber;
  }

  getPhaseLineItemTotal = lineItems =>
    parseFloat((lineItems.reduce((a, b) => a + b.total, 0) || 0).toFixed(2));

  handleSubmit = () => {
    if (!this.validate({ total: true, lineItems: true })) return;
    const { number, total, lineItems, reason, contact } = this.state;
    const finalLineItems = [];
    lineItems.forEach((i) => {
      const editedItem = i;
      editedItem.status = number ? 'Exception - Resolved' : 'Not Reviewed';
      finalLineItems.push(editedItem);
    });
    this.props.updateFormData({
      number,
      total,
      reason,
      contact,
      lineItems: finalLineItems,
    });
  }

  handleAddRow = (e) => {
    e.preventDefault();
    this.setState({
      addRow: !this.state.addRow,
      lineItemId: '',
      kind: '',
      quantity: 0,
      pricePatch: 0,
      lineItemTotal: 0,
      payable: true,
      note: '',
    });
  }

  handleChange = attr => (val) => {
    const { pricePatch, quantity } = this.state;
    let value = this.sanitize(attr, val);
    if (attr === 'payable') value = !value;
    const price = attr === 'pricePatch' ? value : pricePatch;
    const qty = attr === 'quantity' ? value : quantity;
    const lineItemTotal = calculateTotal(price, qty);
    this.setState({ [attr]: value, lineItemTotal });
  }

  handleNumberChange = this.handleChange('number');
  handlePriceChange = this.handleChange('pricePatch');
  handleQuantityChange = this.handleChange('quantity');
  handlePayableChange = this.handleChange('payable');
  handleNoteChange = this.handleChange('note');
  handleReasonChange = this.handleChange('reason');
  handleContactChange =this.handleChange('contact');

  handleNumberChange = e => this.setState({ number: e.target.value });

  handleDescriptionChange = (selection) => {
    const { value, price, payable, kind } = selection;
    const lineItemTotal = calculateTotal(price, this.state.quantity) || 0;
    this.setState({ lineItemId: value, pricePatch: price, payable, lineItemTotal, kind });
  }

  handleSave = (e) => {
    e.preventDefault();
    const { lineItems, lineItemId, kind, quantity, pricePatch,
      lineItemTotal, payable, note } = this.state;
    lineItems.push({
      line_item_id: lineItemId,
      description: this.getSelectedItem(lineItemId).label,
      price: pricePatch,
      total: lineItemTotal,
      quantity: Math.trunc(quantity),
      kind,
      payable,
      note,
    });
    const id = this.getInitialId(lineItems);
    const total = lineItems.length > 0 ? this.getPhaseLineItemTotal(lineItems) : 0;
    this.setState({
      id,
      total,
      lineItems,
      lineItemId: '',
      kind: '',
      quantity: 0,
      pricePatch: 0,
      lineItemTotal: 0,
      payable: true,
      note: '',
    });
  }

  handleDelete = itemNumber => (e) => {
    e.preventDefault();
    const lineItems = this.state.lineItems;
    const filteredItems = filter(lineItems, item => item.line_item_id !== itemNumber);
    const total = filteredItems.length > 0 ? this.getPhaseLineItemTotal(filteredItems) : 0;
    this.setState({ total, lineItems: filteredItems });
  }

  renderTableHead = () => <thead>
    <tr>
      {DISPLAY_KEYS.map(key => (<th
        key={key} className={key === 'Delete' ? S.deleteCell : S.cell}
      >{key}</th>))}
    </tr>
  </thead>

  renderTableBody = lineItems => <tbody>
    {lineItems.map(item => <tr className={S.contentRow} key={item.line_item_id}>
      {this.renderLabel(item.line_item_id)}
      <td className={S.cell}>{item.quantity}</td>
      <td className={S.cell}>{formatDollars(item.price)}</td>
      <td className={S.cell}>{formatDollars(item.total)}</td>
      {this.renderPayable(item.payable)}
      {this.renderNote(item)}
      {this.renderDelete(item.line_item_id)}
    </tr>)}
  </tbody>

  renderLabel = lineItemId => <td className={S.cell}>{this.getSelectedItem(lineItemId).label}</td>

  renderPayable = payable => <td className={S.cell}>
    <FontIcon value={payable ? 'check_box_outline_blank' : 'check_box'} />
  </td>

  renderNote = item => <ItemNote
    display
    label="Note"
    onChange={this.handleNoteChange}
    note={item.note}
    id={item.line_item_id}
    kind="lineItem"
  />

  renderDelete = lineItemId => <td className={S.deleteCell}>
    <IconButton icon="delete" onClick={this.handleDelete(lineItemId)} />
  </td>

  render = () => {
    const { number, reason, contact, total, lineItems, addRow, lineItemId, phase,
      quantity, pricePatch, lineItemTotal, payable, note, errors } = this.state;
    const { lineItemOptions } = this.props;
    const noTableText = addRow ? 'Fill in data below, then click "Save"' : 'Click "ADD ITEM" to start adding line items';
    const invalidNumber = !this.validateNumber(number);
    const disabled = !Array.isArray(lineItems) || !lineItems.length || invalidNumber;
    const message = "Invalid PO Number. If a PO Number is entered, please ensure it is 9 digits or 8 digits prefixed by 'PR' and is not the project number.";
    const submitButton = invalidNumber ? <div className={S.message}>{message}</div> :
      (<Button label="Complete" flat inverse ripple className={S.submit} onClick={this.handleSubmit} disabled={disabled} />);
    return (<div>
      <SimpleInput
        label="PO Number (NOT REQUIRED)"
        text={number}
        onChange={this.handleNumberChange}
      />
      <SimpleInput
        label="Reason for additional money"
        text={reason}
        onChange={this.handleReasonChange}
      />
      <SimpleInput
        label="ISO/IST Contact"
        text={contact}
        onChange={this.handleContactChange}
      />
      {lineItems.length > 0 ? (<div>
        <table className={S.table}>
          {this.renderTableHead()}
          {this.renderTableBody(lineItems)}
        </table>
        <h6 className={S.poTotal}>PO Total: {formatDollars(total)}</h6>
      </div>) : (<div className={S.emptyTable}>
        <h4>{noTableText}</h4>
      </div>)}
      {addRow && <form onSubmit={this.handleSave}>
        <ReactSelect
          label="Number - Description"
          placeholder={'Select...'}
          onChange={this.handleDescriptionChange}
          options={filterByVendor(lineItemOptions, phase.source)}
          value={lineItemId}
        />
        <SimpleInput
          label="QTY"
          text={quantity}
          onChange={this.handleQuantityChange}
          errors={errors.quantity}
        />
        <SimpleInput
          label="Unit Price"
          text={pricePatch}
          onChange={this.handlePriceChange}
          errors={errors.pricePatch}
        />
        {lineItemTotal > 0 && <div>
          <h6>{`Total: ${formatDollars(lineItemTotal)}`}</h6><br />
        </div>}
        <SimpleInput
          label="Note"
          text={note}
          onChange={this.handleNoteChange}
          errors={errors.note}
        />
        <CheckBox
          required
          text="Not Payable"
          errors={errors.payable}
          onChange={this.handlePayableChange}
          checked={!payable}
        />
        <input className={S.save} type="submit" value="Add" />
      </form>}
      <Button className={addRow ? S.cancel : S.add} label={addRow ? 'Cancel' : 'Add Item'} flat inverse ripple onClick={this.handleAddRow} />
      {submitButton}
    </div>);
  }
}

PoDetails.propTypes = {
  phase: PhaseType.isRequired,
  number: PropTypes.string,
  lineItems: PropTypes.array,
  total: PropTypes.number,
  reason: PropTypes.string,
  contact: PropTypes.string,
  errors: PropTypes.shape({
    lineItems: PropTypes.arrayOf(PropTypes.array),
    total: PropTypes.arrayOf(PropTypes.number),
    lineItemId: PropTypes.arrayOf(PropTypes.number),
    quantity: PropTypes.arrayOf(PropTypes.number),
    pricePatch: PropTypes.arrayOf(PropTypes.number),
    lineItemTotal: PropTypes.arrayOf(PropTypes.number),
    payable: PropTypes.arrayOf(PropTypes.bool),
    note: PropTypes.arrayOf(PropTypes.string),
  }),
};

PoDetails.defaultProps = { lineItemOptions: [] };

const mapStateToProps = state => ({
  errors: getPhaseErrors(state), lineItemOptions: getPricePatchItems(state),
});

export default connect(mapStateToProps, {})(PoDetails);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/components/createException/poDetails.jsx